import { isIE11 } from "@fluentui/utilities";
import { IRcaTestResult } from "./models/IRcaTestResult";
import { TrustedScriptURL } from "trusted-types/lib";

/**
 * A function type that takes a test result and returns a partial test result
 * containing the fields with values to be updated
 */
export type RcaResultTransformer = (transform: IRcaTestResult) => Partial<IRcaTestResult>;

/**
 * Utility functions for the app
 */
export class Utils {
  /**
   * Media query to use for mobile device
   */
  public static mobileQuery = "@media only screen and (max-width: 600px)";

  /**
   * Media query to use for non-mobile devices
   */
  public static notMobileQuery = "@media only screen and (min-width: 600px)";

  /**
   * Groups an array of items by a particular value
   *
   * @param items - The items to group
   * @param selector - A selector function to return the string to group by
   * @returns - The grouped items
   */
  public static groupBy<T>(items: T[], selector: (t: T) => string): { [key: string]: T[] } {
    const initial: { [key: string]: T[] } = {};
    return items.reduce((rv, x) => {
      const key = selector(x);
      (rv[key] = rv[key] || []).push(x);
      return rv;
    }, initial);
  }

  /**
   * Returns only the unique items given a selector
   *
   * @param items - The items to process
   * @param selector - A selector function to return the string to define the object's identity
   * @returns - An array of the unique items
   */
  public static unique<T>(items: T[], selector: (t: T) => string): T[] {
    const results: T[] = [];
    const groups = Utils.groupBy(items, selector);
    for (const group of Object.keys(groups)) {
      results.push(groups[group][0]);
    }

    return results;
  }

  /**
   * Gets the values as an array from a dictionary/record
   *
   * @param dict - The object dictionary
   * @returns - the values
   */
  public static dictionaryValues<T>(dict: Record<string, T>): T[] {
    return Object.keys(dict).map((key) => dict[key]);
  }

  /**
   * Delays execution asynchronously for a specified number of milliseconds
   *
   * @param milliseconds - The number of milliseconds to delay
   * @returns - A promise that is resolved when the delay is complete
   */
  public static delayAsync(milliseconds: number): Promise<void> {
    return new Promise<void>((resolve) => {
      setTimeout(() => resolve(), milliseconds);
    });
  }

  /**
   * Escapes a string so it can be used in a regular expression safely
   *
   * @param str - The string to escape
   * @returns The escaped string
   */
  public static escapeRegExp(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  /**
   * Chooses a readable text color for the supplied background.
   *
   * @param backgroundColor - The background color in hex or rgb/rgba format.
   * @returns A readable text color.
   */
  public static getReadableTextColor(backgroundColor: string): string {
    const hexMatch = backgroundColor.match(/^#([0-9a-f]{6})$/i);
    if (hexMatch) {
      const hex = hexMatch[1];
      const red = parseInt(hex.substring(0, 2), 16);
      const green = parseInt(hex.substring(2, 4), 16);
      const blue = parseInt(hex.substring(4, 6), 16);
      const luminance = 0.299 * red + 0.587 * green + 0.114 * blue;

      return luminance > 186 ? "#201f1e" : "#ffffff";
    }

    const rgbMatch = backgroundColor.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
    if (rgbMatch) {
      const red = parseInt(rgbMatch[1], 10);
      const green = parseInt(rgbMatch[2], 10);
      const blue = parseInt(rgbMatch[3], 10);
      const luminance = 0.299 * red + 0.587 * green + 0.114 * blue;

      return luminance > 186 ? "#201f1e" : "#ffffff";
    }

    return "#201f1e";
  }

  /**
   * Loads a script dynamically
   *
   * @param src - The URL of the script to load
   * @param elementId - The element id of the HTML script element that should be created
   * @param createScriptURL - the trusted type scriptUrl factory function
   * @param success - Success callback
   * @param error - Error callback
   */
  public static loadScript(
    src: string,
    elementId: string,
    createScriptURL: (scriptUrl: string) => TrustedScriptURL,
    success: () => void,
    error: () => void
  ): void {
    const script = document.createElement("script");
    const trustedScriptUrl = createScriptURL(src);
    script.src = trustedScriptUrl as unknown as string;
    script.id = elementId;

    if (isIE11()) {
      Utils.pollElementWithTimeout(elementId, 2000, success, error);
    } else {
      script.onload = success;
      script.onerror = error;
    }

    document.body.appendChild(script);
  }

  /**
   * Loads a script dynamically (returns Promise)
   *
   * @param src - The URL of the script to load
   * @param elementId - The element id of the HTML script element that should be created
   * @param createScriptURL - the trusted type scriptUrl factory function
   * @returns - A promise to indicate when the script has loaded or failed
   */
  public static loadScriptAsync(src: string, elementId: string, createScriptURL: (scriptUrl: string) => TrustedScriptURL): Promise<void> {
    return new Promise<void>((resolve, reject) => Utils.loadScript(src, elementId, createScriptURL, resolve, reject));
  }

  /**
   * Creates a new test result tree modified from the original as specified by the transform
   *
   * @param result - The root result to begin our transformation
   * @param transform - The function to modify the properties of each result
   * @returns the new result tree
   */
  public static mapResult(result: IRcaTestResult, transform: RcaResultTransformer): IRcaTestResult {
    // First apply the transformation to all the children to generate new children nodes
    const children: IRcaTestResult[] = result.Children.map((c) => Utils.mapResult(c, transform));

    // This returns a new object, starting with the fields in the original result
    // then it replaces any fields produced by the transformation of the current object
    // And replaces the original children with the transformed children
    return {
      ...result,
      ...transform(result),
      Children: children,
    };
  }

  /**
   * Sets the viewport to the default, which is the device width
   */
  public static setDefaultViewport(): void {
    const viewPort = document.querySelector("meta[name=viewport]");
    if (viewPort) {
      viewPort.setAttribute("content", "width=device-width, initial-scale=1.0");
    }
  }

  /**
   * Sets the viewport to a desktop view
   */
  public static setDesktop(): void {
    const viewPort = document.querySelector("meta[name=viewport]");
    if (viewPort) {
      viewPort.setAttribute("content", "width=1024, initial-scale=0");
    }
  }

  /**
   * Parses a boolean from a string
   *
   * @param str - the string to parse
   * @returns the boolean
   */
  public static parseBoolean(str: string): boolean {
    return str?.toLowerCase() === "true";
  }

  /**
   * Checks whether the value is a string array
   *
   * @param value - the value
   * @returns true if a string array
   */
  public static isStringArray(value: unknown): value is string[] {
    if (value instanceof Array) {
      value.forEach((item) => {
        if (typeof item !== "string") {
          return false;
        }
      });

      return true;
    }

    return false;
  }

  /**
   * Checks whether an object is a keydown event
   *
   * @param e - The event to test
   * @returns - true if the event is a keydown event.
   */
  public static isKeyDownEvent<T>(e: unknown): e is React.KeyboardEvent<T> {
    const eKey = e as React.KeyboardEvent<T>;
    return eKey && typeof eKey.type === "string" && eKey.type === "keydown" && typeof eKey.key === "string";
  }

  /**
   * Checks whether the key exists in a given object
   *
   * @param obj - the object to check
   * @param key - the key to check
   * @returns - true if the object contains this key
   */
  public static hasKey<T>(obj: T, key: string | number | symbol): key is keyof T {
    return typeof key !== "symbol" && (obj as Record<string | number | symbol, string>)[key] !== undefined;
  }

  /**
   * Parses the banner dismissal timestamps
   *
   * @param bannersDismissed - The banner dismissal timestamps
   * @returns - the dismissal timestamps
   */
  public static parseBannerTimestamps(bannersDismissed: Record<string, string>): { [key: string]: Date } {
    const result: { [key: string]: Date } = {};
    for (const key of Object.keys(bannersDismissed)) {
      try {
        if (bannersDismissed[key]) {
          result[key] = new Date(bannersDismissed[key]);
        }
      } catch {
        // Skip if timestamp is invalid
      }
    }

    return result;
  }

  /**
   * Polls for an element to appear in the document for a period of time
   * This is required for IE which doesn't seem to fire script onload/onerror events
   *
   * @param elementId - The element id to query
   * @param timeout - How long it should look for the element
   * @param onSuccess - Success callback
   * @param onTimeout - Timeout callback
   */
  private static pollElementWithTimeout(elementId: string, timeout: number, onSuccess: () => void, onTimeout: () => void): void {
    const start = Date.now();

    /**
     * Polls for the element in a loop
     */
    const poll = (): void => {
      if (document.getElementById(elementId)) {
        onSuccess();
      } else if (Date.now() - start >= timeout) {
        onTimeout();
      } else {
        setTimeout(() => {
          poll();
        }, 100);
      }
    };

    poll();
  }
}
